import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import PlayerSummaryTab from '../components/player/PlayerSummaryTab';
import PlayerPartnershipsTab from '../components/player/PlayerPartnershipsTab';
import PlayerHeadToHeadTab from '../components/player/PlayerHeadToHeadTab';
import { fetchPlayerEloHistory, fetchPlayerRecentGames, fetchPlayerUnifiedGameHistory } from '../services/playerService';
import { fetchPartnershipOverview, fetchTopPartnerships, fetchPartnershipPerformanceHistory } from '../services/partnershipService';
import { getPlayerNameColor } from '../utils/playerUtils';
import Button from '../components/common/Button';
import { BackButton } from '../components/common';
import { Heading, Text, Typography } from '../components/common/Typography';
import { Card, FeatureCard } from '../components/common/Card';

const PlayerDashboard = ({
  currentUser,
  embedded = false,
  activeView = 'summary',
  forceClubId = null,
  initialOpponentId = null,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [clubs, setClubs] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [selectedClub, setSelectedClub] = useState(null);
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [selectedPairType, setSelectedPairType] = useState('all'); // 'all', 'mixed', or 'levels'
  const [eloHistory, setEloHistory] = useState([]);
  const [recentGames, setRecentGames] = useState([]);
  const [allGames, setAllGames] = useState([]);
  const [isLoadingGames, setIsLoadingGames] = useState(false);
  const [playerOptions, setPlayerOptions] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [playerSearchTerm, setPlayerSearchTerm] = useState('');
  const [isPlayerDropdownOpen, setIsPlayerDropdownOpen] = useState(false);
  const playerSearchRef = useRef(null);
  
  // Partnership data state
  const [partnershipOverview, setPartnershipOverview] = useState(null);
  const [topPartnerships, setTopPartnerships] = useState([]);
  const [partnershipPerformanceData, setPartnershipPerformanceData] = useState([]);
  const [isLoadingPartnerships, setIsLoadingPartnerships] = useState(false);
  const [selectedPartnership, setSelectedPartnership] = useState(null);

  // Click outside handler for player dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (playerSearchRef.current && !playerSearchRef.current.contains(event.target)) {
        setIsPlayerDropdownOpen(false);
      }
    };

    if (isPlayerDropdownOpen) {
      // Use click instead of mousedown to allow dropdown clicks to complete first
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [isPlayerDropdownOpen]);

  // Fetch clubs the player is a member of
  useEffect(() => {
    const fetchPlayerClubs = async () => {
      try {
        const { data, error } = await supabase
          .from('players_club')
          .select('club_id, clubs:club_id(id, name)')
          .eq('player_id', currentUser.id);

        if (error) throw error;
        
        const clubsData = data.map(item => item.clubs);
        setClubs(clubsData);
        
        // Set selected club: use forceClubId if provided, then check URL params, otherwise default to first
        if (clubsData.length > 0) {
          // Check URL params for clubId
          const urlParams = new URLSearchParams(location.search);
          const urlClubId = urlParams.get('clubId');
          
          if (forceClubId) {
            const forcedClub = clubsData.find(c => c.id === forceClubId);
            setSelectedClub(forcedClub || clubsData[0]);
          } else if (urlClubId) {
            const urlClub = clubsData.find(c => c.id === urlClubId);
            setSelectedClub(urlClub || clubsData[0]);
          } else {
            setSelectedClub(clubsData[0]);
          }
        }
      } catch (err) {
        console.error('Error fetching player clubs:', err);
        setError('Failed to load clubs. Please try again.');
      } finally {
        if (!currentUser?.id) {
          setIsLoading(false);
        }
      }
    };

    if (currentUser?.id) {
      fetchPlayerClubs();
    } else {
      setIsLoading(false);
    }
  }, [currentUser?.id, forceClubId, location.search]);

  // Fetch seasons for selected club
  useEffect(() => {
    const fetchClubSeasons = async () => {
      if (!selectedClub) {
        setSeasons([]);
        return;
      }
      
      try {
        const { data, error } = await supabase
          .from('seasons')
          .select('*')
          .eq('club_id', selectedClub.id)
          .order('start_date', { ascending: false });

        if (error) throw error;
        
        setSeasons(data);
        
        // Set default selected season to null (all seasons)
        setSelectedSeason(null);
      } catch (err) {
        console.error('Error fetching club seasons:', err);
        setError('Failed to load seasons. Please try again.');
      }
    };

    fetchClubSeasons();
  }, [selectedClub]);

  // Fetch ELO history when filters change
  useEffect(() => {
    const loadEloHistory = async () => {
      if (!currentUser?.id || !selectedClub) {
        setEloHistory([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const history = await fetchPlayerEloHistory(
          currentUser.id,
          selectedClub.id,
          selectedSeason,
          'all' // Always show all pair types
        );
        setEloHistory(history);
      } catch (err) {
        console.error('Error fetching ELO history:', err);
        setError('Failed to load ELO history. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    loadEloHistory();
  }, [currentUser?.id, selectedClub, selectedSeason]);

  // Fetch recent games for the Recent Form component
  useEffect(() => {
    const loadRecentGames = async () => {
      if (!currentUser?.id || !selectedClub) {
        setRecentGames([]);
        return;
      }

      try {
        const games = await fetchPlayerUnifiedGameHistory(
          currentUser.id,
          selectedClub.id,
          selectedSeason,
          10 // Limit to last 10 games
        );
        setRecentGames(games);
      } catch (err) {
        console.error('Error fetching recent games:', err);
        // Don't set error here to avoid disrupting the main dashboard
      }
    };

    loadRecentGames();
  }, [currentUser?.id, selectedClub, selectedSeason]);

  // Fetch all games for the Game History table
  useEffect(() => {
    const loadAllGames = async () => {
      if (!currentUser?.id || !selectedClub) {
        setAllGames([]);
        return;
      }

      setIsLoadingGames(true);
      try {
        // Fetch up to 100 games for pagination
        const games = await fetchPlayerUnifiedGameHistory(
          currentUser.id,
          selectedClub.id,
          selectedSeason,
          100 // Larger limit for game history table
        );
        setAllGames(games);
        
        // Extract unique players from games for the player filter
        const uniquePlayers = new Map();
        games.forEach(game => {
          // Add teammates
          game.teammates?.forEach(player => {
            if (player && player.id) {
              uniquePlayers.set(player.id, {
                id: player.id,
                name: `${player.first_name} ${player.last_name}`,
                gender: player.gender
              });
            }
          });
          
          // Add opponents
          game.opponents?.forEach(player => {
            if (player && player.id) {
              uniquePlayers.set(player.id, {
                id: player.id,
                name: `${player.first_name} ${player.last_name}`,
                gender: player.gender
              });
            }
          });
        });
        
        // Convert Map to array and sort by name
        const playersList = Array.from(uniquePlayers.values()).sort((a, b) => 
          a.name.localeCompare(b.name)
        );
        
        setPlayerOptions(playersList);
      } catch (err) {
        console.error('Error fetching all games:', err);
        // Don't set error here to avoid disrupting the main dashboard
      } finally {
        setIsLoadingGames(false);
      }
    };

    loadAllGames();
  }, [currentUser?.id, selectedClub, selectedSeason]);

  // Fetch partnership data when filters change
  useEffect(() => {
    const loadPartnershipData = async () => {
      if (!currentUser?.id || !selectedClub) {
        setPartnershipOverview(null);
        setTopPartnerships([]);
        setPartnershipPerformanceData([]);
        return;
      }

      setIsLoadingPartnerships(true);
      try {
        // Fetch partnership overview
        const overview = await fetchPartnershipOverview(
          currentUser.id,
          selectedClub.id,
          selectedSeason
        );
        setPartnershipOverview(overview);

        // Fetch top partnerships
        const topPartnershipsData = await fetchTopPartnerships(
          currentUser.id,
          selectedClub.id,
          selectedSeason,
          5
        );
        setTopPartnerships(topPartnershipsData);

        // Fetch partnership performance history
        const performanceData = await fetchPartnershipPerformanceHistory(
          currentUser.id,
          selectedClub.id,
          selectedSeason,
          selectedPartnership?.id
        );
        setPartnershipPerformanceData(performanceData);
      } catch (err) {
        console.error('Error fetching partnership data:', err);
        // Don't set error here to avoid disrupting the main dashboard
      } finally {
        setIsLoadingPartnerships(false);
      }
    };

    loadPartnershipData();
  }, [currentUser?.id, selectedClub, selectedSeason, selectedPartnership]);

  const handleClubChange = (e) => {
    // Don't allow club change when forceClubId is provided (club is set via route)
    if (forceClubId) return;
    
    const clubId = e.target.value;
    const selectedClubObj = clubs.find(club => club.id === clubId);
    setSelectedClub(selectedClubObj || null);
    setSelectedSeason(null); // Reset season when club changes
    setSelectedPlayer(null); // Reset player filter when club changes
    setPlayerSearchTerm(''); // Reset search term
  };

  const handleSeasonChange = (e) => {
    setSelectedSeason(e.target.value === '' ? null : e.target.value);
    setSelectedPlayer(null); // Reset player filter when season changes
    setPlayerSearchTerm(''); // Reset search term
  };

  const handlePairTypeChange = (e) => {
    setSelectedPairType(e.target.value);
  };
  
  const handlePlayerSearch = (e) => {
    setPlayerSearchTerm(e.target.value);
    setIsPlayerDropdownOpen(true);
  };
  
  const handlePlayerSelect = (player, event) => {
    event.preventDefault();
    event.stopPropagation();
    setSelectedPlayer(player);
    setPlayerSearchTerm(player.name);
    setIsPlayerDropdownOpen(false);
  };
  
  const handleClearPlayerFilter = () => {
    setSelectedPlayer(null);
    setPlayerSearchTerm('');
  };
  
  // Games are now unfiltered (no player or pair type filters)
  const filteredGames = allGames;
  const filteredRecentGames = recentGames;
  const filteredEloHistory = eloHistory;

  // Filter player options based on search term
  const filteredPlayerOptions = playerOptions.filter(player => 
    player.name.toLowerCase().includes(playerSearchTerm.toLowerCase())
  );

  const handleViewSessionPerformance = (session) => {
    if (!session || !session.sessionId || !session.clubId || !currentUser?.id) return;
    // Navigate to the session performance page
    navigate(`/player/${currentUser.id}/session/${session.sessionId}`, {
      state: { clubId: session.clubId }
    });
  };

  if (!currentUser) {
    return (
      <div className="container mx-auto px-6 py-8 max-w-app-shell">
        <Card variant="warning" size="large" className="text-center">
          <Heading level={2} className="mb-4">Login Required</Heading>
          <Typography variant="warning" className="mb-6">Please log in to view your player dashboard.</Typography>
          <Button onClick={() => navigate('/')} variant="primary">
            Go to Home
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className={embedded ? '' : 'container mx-auto px-6 py-8 max-w-app-shell'}>
      {/* Back Button and main header are handled by PlayerProfilePage when embedded */}
      {!embedded && (
        <>
          <div className="mb-8">
            <BackButton onClick={() => navigate('/')}>
              Back to Home
            </BackButton>
          </div>
          
          <div className="text-center mb-16">
            <Heading level={1} className="text-display mb-6">Player Dashboard</Heading>
            <Text className="text-body-large max-w-2xl mx-auto">
              Track your performance, partnerships, and matchup statistics
            </Text>
          </div>
        </>
      )}
      
      {error && (
        <Card variant="danger" size="medium" className="mb-8">
          <Typography variant="error">{error}</Typography>
        </Card>
      )}
      
      {/* Top-level season selection - only show for Performance view when not embedded */}
      {activeView === 'summary' && !embedded && (
      <Card variant="default" size="medium" className="mb-8 overflow-visible">
        <Heading level={3} className="mb-6">Season</Heading>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Season Filter (applies to all performance data, including sessions) */}
          <div>
            <Typography variant="label" className="block mb-1">
              Season
            </Typography>
            <select
              value={selectedSeason || ''}
              onChange={handleSeasonChange}
              className="w-full border border-slate-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-slate-500"
              disabled={isLoading || !selectedClub}
            >
              <option value="">All Seasons</option>
              {seasons.map((season) => (
                <option key={season.id} value={season.id}>
                  {season.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Card>
      )}
      
      {/* Main content: view determined by parent via activeView */}
      {activeView === 'summary' && (
              <PlayerSummaryTab
                filteredRecentGames={filteredRecentGames}
                filteredEloHistory={filteredEloHistory}
                filteredGames={filteredGames}
          baseClubGames={allGames}
                isLoading={isLoading}
                isLoadingGames={isLoadingGames}
          currentPlayerId={currentUser?.id}
          onViewSession={handleViewSessionPerformance}
          clubId={selectedClub?.id}
        />
      )}

      {activeView === 'partnerships' && (
              <PlayerPartnershipsTab
                partnershipOverview={partnershipOverview}
                topPartnerships={topPartnerships}
                partnershipPerformanceData={partnershipPerformanceData}
                isLoadingPartnerships={isLoadingPartnerships}
                currentPlayerId={currentUser?.id}
                selectedPartnership={selectedPartnership}
              />
      )}

      {activeView === 'headtohead' && (
              <PlayerHeadToHeadTab
                currentPlayerId={currentUser?.id}
                selectedClub={selectedClub}
                selectedSeason={selectedSeason}
                initialOpponentId={initialOpponentId}
              />
      )}

    </div>
  );
};

export default PlayerDashboard; 